/**
 * 轴分割
 *
 * @see https://echarts.apache.org/zh/option.html#xAxis
 * @see https://echarts.apache.org/zh/option.html#yAxis
 *
 * 新增以下配置项
 * * 配置分割线条数：`dvSplitLineNumber`
 * * 配置多根轴 0 刻度对齐：`dvAlignZeroTick`
 * * 配置轴两侧的留白是否包含 0 刻度：`dvBoundaryGapContainZeroTick`
 *
 * - 区别于官方的 splitNumber 配置项，并具有比 splitNumber 更高的优先级
 * - 当前实现仅对 type: 'value' 轴有效
 *
 * - 1. 分割线数目固定
 * - 2. 要有 0 刻度
 * - 3. 多个轴的 0 刻度对齐
 */
import Grid from 'echarts/lib/coord/cartesian/Grid';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type Axis2D from 'echarts/types/src/coord/cartesian/Axis2D';
import { round } from 'echarts/lib/util/number';
import { parsePercent } from 'zrender/lib/contain/text';
import { isInt, nice } from '../../../util/math';

function isValidExtent(axis: Axis2D) {
  const dataExtent = axis.scale.getExtent();
  return (
    Number.isFinite(dataExtent[0]) &&
    Number.isFinite(dataExtent[1]) &&
    dataExtent[0] <= dataExtent[1]
  );
}

function isUserConfig(axis: Axis2D) {
  return (
    (typeof axis?.model?.get('interval') !== 'undefined' &&
      (typeof axis?.model?.get('__cacheInterval') === 'undefined' ||
        axis?.model?.get('interval') !== axis?.model?.get('__cacheInterval'))) ||
    (typeof axis?.model?.get('min') !== 'undefined' &&
      (typeof axis?.model?.get('__cacheMin') === 'undefined' ||
        axis?.model?.get('min') !== axis?.model?.get('__cacheMin'))) ||
    (typeof axis?.model?.get('max') !== 'undefined' &&
      (typeof axis?.model?.get('__cacheMax') === 'undefined' ||
        axis?.model?.get('max') !== axis?.model?.get('__cacheMax')))
  );
}

function needProcessSplitLineNumber(axis: Axis2D) {
  return !(
    !isValidExtent(axis) ||
    axis?.scale?.type !== 'interval' ||
    !(Number.isFinite(axis.scale.getExtent()[0]) && Number.isFinite(axis.scale.getExtent()[1])) ||
    // ! 需要和其它配置项一起生效
    (typeof axis?.model?.get('dvSplitLineNumber') === 'undefined' &&
      !axis.model.get('alignTicks') &&
      !axis?.model?.get('dvAlignZeroTick')) ||
    // ! 用户侧配置优先级更高
    isUserConfig(axis)
  );
}

function clearCache(axis: Axis2D) {
  delete axis.model.option.__cacheMin;
  delete axis.model.option.__cacheMax;
  delete axis.model.option.__cacheInterval;
}

function setAndUpdateCache(axis: Axis2D, { min, max, interval }) {
  if (Number.isNaN(min) || Number.isNaN(max) || Number.isNaN(interval)) {
    return;
  }

  axis.model.option.min = min;
  axis.model.option.max = max;
  axis.model.option.interval = interval;

  axis.model.option.__cacheMin = min;
  axis.model.option.__cacheMax = max;
  axis.model.option.__cacheInterval = interval;
}

/**
 * 计算轴的两侧留白空间
 *
 * ! 代码拷贝自 echarts 源码，保证和原有的 boundaryGap 配置项解析逻辑一致
 * @see https://github.com/apache/echarts/blob/5.3.3/src/coord/scaleRawExtentInfo.ts#L132
 */
function calculateBoundaryGap(axis: Axis2D) {
  let boundaryGapInner = [0, 0];

  const boundaryGap = axis.model.get('boundaryGap');
  const boundaryGapArr = Array.isArray(boundaryGap)
    ? boundaryGap
    : [boundaryGap || 0, boundaryGap || 0];

  if (typeof boundaryGapArr[0] === 'boolean' || typeof boundaryGapArr[1] === 'boolean') {
    boundaryGapInner = [0, 0];
  } else {
    boundaryGapInner = [parsePercent(boundaryGapArr[0], 1), parsePercent(boundaryGapArr[1], 1)];
  }

  return boundaryGapInner;
}

/**
 * 获取包含 boundaryGap 的轴范围
 */
// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
function getExtentWithBoundaryGap(axis: Axis2D): [number, number] {
  const dataExtent = axis.scale.getExtent();
  const needCrossZero = axis.model.getNeedCrossZero?.();
  const extentOnCrossZero = axis?.model?.get('dvBoundaryGapContainZeroTick');
  if (needCrossZero && extentOnCrossZero) {
    if (dataExtent[0] < 0 && dataExtent[1] < 0) {
      dataExtent[1] = 0;
    }
    if (dataExtent[0] > 0 && dataExtent[1] > 0) {
      dataExtent[0] = 0;
    }
  }

  // https://github.com/apache/echarts/blob/5.3.3/src/coord/scaleRawExtentInfo.ts#L177
  const dataSpan = dataExtent[1] - dataExtent[0] || Math.abs(dataExtent[0]);
  // ! 这里的实现和 ecahrts 保持一致，取数据原始范围的百分比
  const boundaryGapInner = calculateBoundaryGap(axis).map(item => dataSpan * item);
  // https://github.com/apache/echarts/blob/5.3.3/src/coord/scaleRawExtentInfo.ts#L190
  let [min, max] = [dataExtent[0] + -boundaryGapInner[0], dataExtent[1] + boundaryGapInner[1]];

  // fix cross zero tick
  // https://github.com/apache/echarts/blob/5.3.3/src/coord/scaleRawExtentInfo.ts#L209
  if (needCrossZero) {
    if (min < 0 && max < 0) {
      max = 0;
    }
    if (min > 0 && max > 0) {
      min = 0;
    }
  }

  return [min, max];
}

function isValidSplitLineNumber(dvSplitLineNumber: number) {
  return !(
    typeof dvSplitLineNumber === 'undefined' ||
    Number.isNaN(dvSplitLineNumber) ||
    dvSplitLineNumber <= 1
  );
}

function niceAxisExtentOnOneSplitLine({
  originExtent,
  min,
  max,
  interval
}: {
  originExtent: [number, number];
  min: number;
  max: number;
  interval: number;
}) {
  // ! 对于 splitNumber === 1 这种特殊情况，需要按 min，max 直接确定最终范围
  max = max === 0 ? 0 : nice(max);

  if (min === 0) {
    min = 0;
  } else if (min < 0) {
    min = -nice(Math.abs(min));
  } else {
    min = nice(min, true);
    if (originExtent[0] < min) {
      min = nice(min / 2, true);
    }
  }

  interval = max - min;

  return { min, max, interval };
}

/**
 * 将轴分割线固定在对应的条数
 */
// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
function niceAxisExtent(axis: Axis2D, alignTo?: Axis2D) {
  const dvSplitLineNumber = (alignTo || axis)?.model?.get('dvSplitLineNumber') as number;
  if (!isValidSplitLineNumber(dvSplitLineNumber)) {
    return;
  }

  const splitNumber = dvSplitLineNumber - 1;
  const originExtent = getExtentWithBoundaryGap(axis);
  let [min, max] = originExtent;
  let interval: number;

  // ! 处理极值一样时，无法分割的情况，将数据区间手动扩大以进行分割
  if (min === max) {
    const extend = Math.max(Math.abs(min), 1);
    min -= extend / 2;
    max += extend / 2;
  }

  if (splitNumber > 1) {
    let toSplitNumber = splitNumber;
    let fixedSplitNumber = Infinity;

    // ! toSplitNumber === 1 时已达到最小分割数量了，此时不应再向下舍去，否则会出现 fixedSplitNumber > splitNumber 的情况
    while (fixedSplitNumber > splitNumber && toSplitNumber > 0) {
      // 设置 round=true 是为了让 interval 尽可能的小
      interval = nice((max - min) / toSplitNumber, toSplitNumber > 1);
      fixedSplitNumber = Math.ceil(max / interval) - Math.floor(min / interval);

      // * 但这里要考虑如果只是为了让 interval 尽可能小一点而要再迭代一次，导致最终结果可能 interval 比预期的要大
      // 例如，目标分割条数为 4，数据范围为 0 - 29，第一轮迭代 (29-0)/4=7.25，按 round=true 时，需要再迭代一轮，最终会是 (29-0)/3 = 10，实际上 8 更符合预期
      const intervalTmp = nice((max - min) / toSplitNumber);
      const fixedSplitNumberTmp = Math.ceil(max / intervalTmp) - Math.floor(min / intervalTmp);
      if (fixedSplitNumberTmp === splitNumber && intervalTmp - interval < interval) {
        interval = intervalTmp;
        fixedSplitNumber = fixedSplitNumberTmp;
      }

      toSplitNumber -= 1;
    }

    min = round(Math.floor(min / interval) * interval);
    max = round(Math.ceil(max / interval) * interval);

    while (fixedSplitNumber < splitNumber) {
      if (min === 0) {
        max += interval;
      } else if (max === 0) {
        min += -interval;
      } else if (originExtent[0] - min > max - originExtent[1]) {
        max += interval;
      } else {
        min += -interval;
      }
      fixedSplitNumber += 1;
    }
  } else {
    ({ min, max, interval } = niceAxisExtentOnOneSplitLine({ originExtent, min, max, interval }));
  }

  setAndUpdateCache(axis, { min, max, interval });
}

/**
 * 计算区间大小和 0 上和 0 下分割数量，目的是取尽可能小的区间大小来确定最终的区间分布情况
 */
function calculateIntervalAndDistributions(
  averageBelowZero: number,
  averageAboveZero: number,
  splitNumber: number,
  min: number,
  max: number
) {
  // 如果分割线数目已经是整数，那么区间大小取 0 上和 0 下里面最大的一个即可
  if (isInt(averageBelowZero)) {
    const interval = Math.max(
      // ! 要注意考虑如果数据都在 0 轴的同一侧，那么无数据的一侧区间大小直接取 0 即可，否则会导致算出来的区间值比预期的要大
      averageAboveZero === 0 ? 0 : nice(max / averageAboveZero),
      averageBelowZero === 0 ? 0 : nice(-min / averageBelowZero)
    );

    return [averageAboveZero, averageBelowZero, interval];
  }

  // 如果分割线数目是小数，分两次（向上取整和向下取整）分别计算区间大小，取最小的即可
  const averageBelowZero1 = Math.floor(averageBelowZero);
  const averageAboveZero1 = splitNumber - averageBelowZero1;
  const interval1 = Math.max(nice(max / averageAboveZero1), nice(-min / averageBelowZero1));

  const averageBelowZero2 = Math.ceil(averageBelowZero);
  const averageAboveZero2 = splitNumber - averageBelowZero2;
  const interval2 = Math.max(nice(max / averageAboveZero2), nice(-min / averageBelowZero2));

  if (interval1 < interval2) {
    return [averageAboveZero1, averageBelowZero1, interval1];
  }
  return [averageAboveZero2, averageBelowZero2, interval2];
}

/**
 * 多根轴在已经对齐刻度的基础上再对齐 0 刻度
 */
function alignZeroTick(axisNeedsAlignZeroTick: Axis2D[], alignTo: Axis2D) {
  const splitLineNumber = alignTo?.model?.get('dvSplitLineNumber') as number;
  if (typeof splitLineNumber === 'undefined' || !alignTo?.model?.get('interval')) {
    return;
  }

  const splitNumber = splitLineNumber - 1;

  axisNeedsAlignZeroTick.push(alignTo);

  // 在已经对齐刻度的基础上统计分割区间在 0 上 0 下的分布情况
  let minTotal = 0;
  let maxTotal = 0;
  let belowZeroSplitNumber = 0;

  axisNeedsAlignZeroTick.forEach(axis => {
    minTotal += axis.model.get('min') as number;
    maxTotal += axis.model.get('max') as number;
    belowZeroSplitNumber += Math.floor(
      Math.abs(axis.model.get('min')) / axis.model.get('interval')
    );
  });

  // 在分割区间数量已经一样的情况下，若都在 0 轴同一侧，那么已经实现了 0 轴对齐
  if (minTotal === 0 || maxTotal === 0) {
    return;
  }

  // 根据多根轴的分割区间在 0 上 0 下的平均分布先计算参照轴的分割区间
  // ! 分割区间数量一侧为 0 - 1 之间时必须取 1，不能取 0
  let avgBelowZero = belowZeroSplitNumber / axisNeedsAlignZeroTick.length;
  if (avgBelowZero > 0) {
    avgBelowZero = Math.max(avgBelowZero, 1);
  }
  let avgAboveZero = splitNumber - avgBelowZero;
  if (avgAboveZero > 0) {
    avgAboveZero = Math.max(avgAboveZero, 1);
    avgBelowZero = splitNumber - avgAboveZero;
  }

  const [min, max] = getExtentWithBoundaryGap(alignTo);
  const [alignToAboveZero, alignToBelowZero] = calculateIntervalAndDistributions(
    avgBelowZero,
    avgAboveZero,
    splitNumber,
    min,
    max
  );

  // 以参照轴的 0 上 0 下分割区间分布确定所有要对齐 0 刻度轴的区间分布和区间大小
  axisNeedsAlignZeroTick.forEach(axis => {
    let [minTmp, maxTmp] = getExtentWithBoundaryGap(axis);
    const [, , interval] = calculateIntervalAndDistributions(
      alignToBelowZero,
      alignToAboveZero,
      splitNumber,
      minTmp,
      maxTmp
    );
    minTmp = -round(alignToBelowZero * interval);
    maxTmp = round(alignToAboveZero * interval);

    // reset
    setAndUpdateCache(axis, { min: minTmp, max: maxTmp, interval });
  });
}

/**
 * 实现多根轴刻度对齐
 */
function alignScaleTicks(axisNeedsAlign: Axis2D[], alignTo: Axis2D) {
  const splitLineNumber = alignTo?.model?.get('dvSplitLineNumber') as number;
  if (typeof splitLineNumber === 'undefined' || !alignTo?.model?.get('interval')) {
    return;
  }

  const splitNumber = splitLineNumber - 1;
  const axisNeedsAlignZeroTick: Axis2D[] = [];
  axisNeedsAlign.forEach(axis => {
    niceAxisExtent(axis, alignTo);

    if (
      alignTo.model.getNeedCrossZero?.() &&
      axis.model.getNeedCrossZero?.() &&
      axis.model.get('dvAlignZeroTick')
    ) {
      axisNeedsAlignZeroTick.push(axis);
    }
  });

  if (
    // ! 在数据范围跨 0 刻度时，至少要存在两个分割区域才能保证 0 刻度一定存在
    splitNumber > 1 &&
    alignTo.model.getNeedCrossZero?.() &&
    axisNeedsAlignZeroTick.length
  ) {
    alignZeroTick(axisNeedsAlignZeroTick, alignTo);
  }
}

type AxesMap = {
  x: Axis2D[];
  y: Axis2D[];
};

// eslint-disable-next-line @typescript-eslint/unbound-method
const originGridUpdateMethod = Grid.prototype.update;

function gridUpdate(this: Grid, ecModel: GlobalModel, api: ExtensionAPI, ...rest: unknown[]) {
  const axesMap = this._axesMap as AxesMap;

  // ! 为了获取到真实数据的范围
  this._updateScale(ecModel, this.model);

  function preprocess(axes: Record<number, Axis2D>) {
    if (!axes) {
      return;
    }

    let alignTo: Axis2D;
    const axisNeedsAlign: Axis2D[] = [];

    (Object.keys(axes) as unknown as number[]).forEach(axesIndex => {
      const axis = axes[axesIndex];

      if (!needProcessSplitLineNumber(axis)) {
        /**
         * ! 某一点无数据时，要把max/min/interval都删掉
         * 否则会出现max/min/interval存在，但是cache不存在的情况，这会导致始终走不到后面的y轴处理逻辑，出现y轴刻度不再动态变化的现象
         * */
        if (axis?.model?.get('max') === axis?.model?.get('__cacheMax')) {
          delete axis.model.option.max;
        }
        if (axis?.model?.get('min') === axis?.model?.get('__cacheMin')) {
          delete axis.model.option.min;
        }
        if (axis?.model?.get('interval') === axis?.model?.get('__cacheInterval')) {
          delete axis.model.option.interval;
        }

        clearCache(axis);

        return;
      }

      clearCache(axis);

      // ! 多根轴刻度对齐策略和 echarts 的实现保持一致
      if (axis.model.get('alignTicks')) {
        axisNeedsAlign.push(axis);
      } else {
        niceAxisExtent(axis);
        alignTo = axis;
      }
    });

    if (axisNeedsAlign.length) {
      // ! 多根轴刻度对齐的参考轴选取和 echarts 实现保持一致
      if (!alignTo) {
        alignTo = axisNeedsAlign.pop();
        niceAxisExtent(alignTo);
      }

      alignScaleTicks(axisNeedsAlign, alignTo);
    }

    //
  }

  preprocess(axesMap.x);
  preprocess(axesMap.y);

  // @ts-ignore
  originGridUpdateMethod.call(this, ecModel, api, ...rest);
}

export function install() {
  // ! 覆盖原有的实现
  // @ts-ignore
  Grid.prototype.update = gridUpdate;
}
